import { useEffect, useMemo, useState } from "react";
import type { PublicPublication } from "./ConfigContext";
import { ConfigContext } from "./ConfigContext";
import { LoadingSpinner } from "../../bits/loading";
import { Amplify } from "aws-amplify";
import outputs from "../WithAuth/auth/amplify_config";
import { css } from "@emotion/react";

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const apiUrl = useMemo(
    () => import.meta.env.VITE_API_URL ?? window.location.origin,
    [],
  );

  const [config, setConfig] = useState<{
    userPoolId: string;
    clientId: string;
    assetBucketUrl: string;
    apiUrl: string;
    mixpanelEnabled: boolean;
    msalClientId?: string;
    aiSearchApiUrl: string;
    frjPublicationsToken: string;
    loginPageContent?: string;
  }>();
  const [error, setError] = useState<string | null>(null);
  const [publicPublications, setPublicPublications] = useState<
    PublicPublication[] | null
  >(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const url = `${apiUrl}/_config`;
        const response = await fetch(url);
        const body = await response.json();
        if (
          !body.userPoolId ||
          !body.clientId ||
          !body.assetBucketUrl ||
          !body.domain
        ) {
          throw new Error("Missing required config values");
        }
        setConfig({ ...body, apiUrl });
        Amplify.configure(outputs(body.userPoolId, body.clientId, body.domain));
      } catch (error) {
        console.error("Error fetching config values:", error);
        setError("Failed to load configuration. Please try again later.");
      }
    };
    fetchConfig();
  }, [apiUrl]);

  useEffect(() => {
    if (config?.frjPublicationsToken) {
      // get publication from public frj endpoint for image background
      try {
        const fetchPublications = async () => {
          const response = await fetch(
            `${config.apiUrl}/frj-publications?token=${config.frjPublicationsToken}`,
          );
          if (!response.ok) {
            throw new Error(
              `Failed to fetch FRJ publications: ${response.statusText}`,
            );
          }
          const publications = await response.json();
          setPublicPublications(publications);
        };
        fetchPublications();
      } catch (error) {
        console.error("Error fetching FRJ publications:", error);
      }
    }
  }, [config?.apiUrl, config?.frjPublicationsToken]);

  if (error) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
        `}
      >
        <h1>Error</h1>
        <h2>{error}</h2>
      </div>
    );
  }

  if (!config) {
    return <LoadingSpinner />;
  }
  return (
    <ConfigContext.Provider
      value={{
        ...config,
        publicPublications,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
